/**
 * TUVI - Pólizas por Proyecto
 * Lista los proyectos (vendido → contratado → en ejecución) con su conteo de
 * pólizas y alerta (en ejecución sin pólizas). Al entrar a un proyecto se
 * pueden subir hasta 5 pólizas, cada una con un título obligatorio.
 * Archivos en bucket MinIO "polizas".
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import client from '../../services/graphqlClient';
import { useNotification } from '../../context/NotificationContext';
import { UPLOAD_URL } from '../../config';
import './Polizas.css';

const MAX_POLIZAS = 5;

const ESTADOS = {
  vendido:                { label: 'Vendido', color: '#06b6d4' },
  contratado_no_iniciado: { label: 'Contratado', color: '#6366f1' },
  en_ejecucion:           { label: 'En Ejecución', color: '#22c55e' },
};

const formatBytes = (b) => {
  const n = Number(b || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};
const iconoTipo = (mime, nombre) => {
  const ext = (nombre || '').split('.').pop().toLowerCase();
  if ((mime || '').startsWith('image/')) return '🖼';
  if (mime === 'application/pdf' || ext === 'pdf') return '📄';
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['xls', 'xlsx'].includes(ext)) return '📊';
  return '📎';
};

export default function Polizas() {
  const { success, error: showError } = useNotification();
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [seleccionado, setSeleccionado] = useState(null); // resumen del proyecto abierto

  const cargarProyectos = useCallback(async () => {
    try {
      setLoading(true);
      const d = await client.query(`{
        proyectosPolizas {
          id proyecto_nombre nombre_cliente apartamento estado valor_final total_polizas alerta
        }
      }`);
      setProyectos(d.proyectosPolizas || []);
    } catch {
      showError('Error cargando proyectos');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { cargarProyectos(); }, [cargarProyectos]);

  const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const filtrados = useMemo(() => {
    let lista = proyectos;
    if (filtroEstado === 'alerta') lista = lista.filter(p => p.alerta);
    else if (filtroEstado !== 'todos') lista = lista.filter(p => p.estado === filtroEstado);
    const q = norm(busqueda.trim());
    if (q) lista = lista.filter(p =>
      norm(p.proyecto_nombre).includes(q) || norm(p.nombre_cliente).includes(q) || norm(p.apartamento).includes(q));
    return lista;
  }, [proyectos, filtroEstado, busqueda]);

  const conteo = useMemo(() => {
    const c = { todos: proyectos.length, alerta: proyectos.filter(p => p.alerta).length };
    proyectos.forEach(p => { c[p.estado] = (c[p.estado] || 0) + 1; });
    return c;
  }, [proyectos]);

  // Al cerrar el detalle, refrescar la lista (conteos/alertas actualizados)
  const cerrarDetalle = () => { setSeleccionado(null); cargarProyectos(); };

  if (seleccionado) {
    return <PolizasDetalle proyecto={seleccionado} onVolver={cerrarDetalle} />;
  }

  return (
    <div className="pol-module">
      <div className="pol-header">
        <div>
          <h1>Pólizas</h1>
          <p>Pólizas por proyecto · vendido, contratado y en ejecución</p>
        </div>
      </div>

      {/* Buscador */}
      <div className="pol-toolbar">
        <div className="pol-search-wrap">
          <span className="pol-search-icon">🔍</span>
          <input className="pol-search" type="text" placeholder="Buscar proyecto, cliente o apartamento..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        </div>
        <span className="pol-count">{filtrados.length} de {proyectos.length} proyectos</span>
      </div>

      {/* Filtros */}
      <div className="pol-filtros">
        <button className={`pol-filtro${filtroEstado === 'todos' ? ' active' : ''}`} onClick={() => setFiltroEstado('todos')}>
          Todos <span className="pol-filtro-count">{conteo.todos}</span>
        </button>
        {Object.entries(ESTADOS).map(([key, cfg]) => (
          <button key={key}
            className={`pol-filtro${filtroEstado === key ? ' active' : ''}`}
            style={filtroEstado === key ? { borderColor: cfg.color, background: cfg.color + '18', color: cfg.color } : {}}
            onClick={() => setFiltroEstado(key)}>
            <span className="pol-filtro-dot" style={{ background: cfg.color }} />
            {cfg.label}{conteo[key] ? <span className="pol-filtro-count">{conteo[key]}</span> : null}
          </button>
        ))}
        <button className={`pol-filtro pol-filtro-alerta${filtroEstado === 'alerta' ? ' active' : ''}`} onClick={() => setFiltroEstado('alerta')}>
          ⚠️ Sin póliza (En Ejecución){conteo.alerta ? <span className="pol-filtro-count">{conteo.alerta}</span> : null}
        </button>
      </div>

      {loading ? (
        <div className="pol-loading">Cargando...</div>
      ) : filtrados.length === 0 ? (
        <div className="pol-empty">No se encontraron proyectos</div>
      ) : (
        <div className="pol-grid">
          {filtrados.map(p => {
            const cfg = ESTADOS[p.estado] || { label: p.estado, color: '#94a3b8' };
            return (
              <div key={p.id} className={`pol-card${p.alerta ? ' pol-card-alerta' : ''}`} onClick={() => setSeleccionado(p)}>
                <div className="pol-card-top">
                  <span className="pol-card-estado" style={{ background: cfg.color }}>{cfg.label}</span>
                  {p.alerta && <span className="pol-card-warn" title="En ejecución sin pólizas">⚠️</span>}
                </div>
                <div className="pol-card-nombre" title={p.proyecto_nombre}>{p.proyecto_nombre}</div>
                <div className="pol-card-cliente">{p.nombre_cliente}</div>
                <div className="pol-card-foot">
                  <span className="pol-card-apto">{p.apartamento || 's/apto'}</span>
                  <span className={`pol-card-badge${p.total_polizas > 0 ? ' has' : ''}`}>
                    {p.total_polizas}/{MAX_POLIZAS} pólizas
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Detalle de un proyecto: lista + subida de pólizas
// ============================================================================
function PolizasDetalle({ proyecto, onVolver }) {
  const { success, error: showError } = useNotification();
  const [polizas, setPolizas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const fileInputRef = useRef(null);
  const [modal, setModal] = useState({ visible: false, file: null, titulo: '' });

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const d = await client.query(
        `query($pid: Int!) { polizasProyecto(proyecto_id: $pid) { id proyecto_id titulo nombre_archivo key_minio mime_type tamano_bytes created_at } }`,
        { pid: proyecto.id }
      );
      setPolizas(d.polizasProyecto || []);
    } catch (e) {
      showError('Error cargando pólizas: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [proyecto.id, showError]);

  useEffect(() => { cargar(); }, [cargar]);

  const onFileSelected = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setModal({ visible: true, file: f, titulo: '' });
    e.target.value = '';
  };

  const confirmarSubida = async () => {
    const { file, titulo } = modal;
    if (!file) return;
    if (!titulo.trim()) { showError('El título de la póliza es obligatorio'); return; }
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('proyecto_id', proyecto.id);
      fd.append('titulo', titulo.trim());
      const token = localStorage.getItem('tuvi_token');
      const r = await fetch(`${UPLOAD_URL}/poliza`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd,
      });
      const json = await r.json();
      if (!json.success) throw new Error(json.message || 'Error subiendo póliza');
      success('Póliza subida');
      setModal({ visible: false, file: null, titulo: '' });
      cargar();
    } catch (e) {
      showError('Error: ' + e.message);
    } finally {
      setSubiendo(false);
    }
  };

  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar la póliza "${p.titulo}"? Esta acción no se puede deshacer.`)) return;
    try {
      const r = await client.mutate(`mutation($id: Int!) { eliminarPolizaProyecto(id: $id) { success message } }`, { id: p.id });
      if (r.eliminarPolizaProyecto.success) { success('Póliza eliminada'); cargar(); }
      else showError(r.eliminarPolizaProyecto.message);
    } catch (e) { showError('Error: ' + e.message); }
  };

  const urlInline = (p) => `${UPLOAD_URL}/poliza/${encodeURIComponent(p.key_minio)}?inline=1`;
  const urlDownload = (p) => `${UPLOAD_URL}/poliza/${encodeURIComponent(p.key_minio)}`;
  const lleno = polizas.length >= MAX_POLIZAS;

  return (
    <div className="pol-module">
      <div className="pol-detalle-head">
        <button className="pol-btn-volver" onClick={onVolver}>← Volver</button>
        <div>
          <h1>{proyecto.proyecto_nombre}</h1>
          <p>{proyecto.nombre_cliente} · Apto {proyecto.apartamento || 's/n'} · {ESTADOS[proyecto.estado]?.label || proyecto.estado}</p>
        </div>
      </div>

      <div className="pol-detalle-bar">
        <span className="pol-detalle-info">{polizas.length} / {MAX_POLIZAS} pólizas cargadas</span>
        <button className="pol-btn pol-btn-primary" disabled={lleno} onClick={() => fileInputRef.current?.click()}>
          {lleno ? 'Máximo alcanzado (5)' : '+ Subir póliza'}
        </button>
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={onFileSelected} />
      </div>

      {loading ? (
        <div className="pol-loading">Cargando pólizas...</div>
      ) : polizas.length === 0 ? (
        <div className="pol-empty">
          <p>No hay pólizas cargadas para este proyecto.</p>
          {proyecto.estado === 'en_ejecucion' && (
            <p className="pol-empty-alert">⚠️ Este proyecto está En Ejecución y aún no tiene pólizas.</p>
          )}
        </div>
      ) : (
        <div className="pol-list">
          {polizas.map(p => (
            <div key={p.id} className="pol-item">
              <div className="pol-item-icon">{iconoTipo(p.mime_type, p.nombre_archivo)}</div>
              <div className="pol-item-body">
                <div className="pol-item-titulo">{p.titulo}</div>
                <div className="pol-item-meta">
                  <span>{p.nombre_archivo}</span>
                  {p.tamano_bytes > 0 && <span>· {formatBytes(p.tamano_bytes)}</span>}
                  <span>· {formatDate(p.created_at)}</span>
                </div>
              </div>
              <div className="pol-item-acc">
                <button className="pol-icon-btn" onClick={() => window.open(urlInline(p), '_blank', 'noopener,noreferrer')} title="Ver">👁</button>
                <a className="pol-icon-btn" href={urlDownload(p)} download={p.nombre_archivo} title="Descargar">⬇</a>
                <button className="pol-icon-btn pol-icon-del" onClick={() => eliminar(p)} title="Eliminar">🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal subir póliza */}
      {modal.visible && (
        <div className="pol-modal-overlay" onClick={() => setModal({ visible: false, file: null, titulo: '' })}>
          <div className="pol-modal" onClick={e => e.stopPropagation()}>
            <h3>Subir póliza</h3>
            <div className="pol-modal-file">
              <span className="pol-modal-file-icon">{iconoTipo(modal.file?.type, modal.file?.name)}</span>
              <div>
                <div className="pol-modal-file-name">{modal.file?.name}</div>
                <div className="pol-modal-file-meta">{formatBytes(modal.file?.size)}</div>
              </div>
            </div>
            <label className="pol-modal-label">Título de la póliza <span style={{ color: '#dc2626' }}>*</span></label>
            <input className="pol-modal-input" type="text" value={modal.titulo} autoFocus
              onChange={e => setModal(prev => ({ ...prev, titulo: e.target.value }))}
              placeholder="Ej: Póliza de cumplimiento, Garantía, RC..." />
            <div className="pol-modal-actions">
              <button className="pol-btn pol-btn-secondary" onClick={() => setModal({ visible: false, file: null, titulo: '' })} disabled={subiendo}>Cancelar</button>
              <button className="pol-btn pol-btn-primary" onClick={confirmarSubida} disabled={subiendo || !modal.titulo.trim()}>
                {subiendo ? 'Subiendo…' : 'Subir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
